import React, { useEffect, useState, useRef, useMemo } from "react";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { BodyMap } from "@/components/booking/BodyMap";
import { MedicalAlerts } from "@/components/booking/MedicalAlerts";
import { QuickNotes } from "@/components/booking/QuickNotes";
import { ClipboardList, Plus, Copy, Trash2, Save, History, ImagePlus, X, Stethoscope, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const photoSrc = (id) => `${API_BASE}/clients/photos/${id}/file?auth=${localStorage.getItem("pc_token")}`;
const TYPE_LABEL = { laser: "Laser / Hair", dermal: "Dermal", injectable: "Injectable", generic: "Notes" };
const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

// mode: "list" (full tab) | "visit" (one "+ Chart" button for a booking) | "hidden" (editor only, opened via openId)
export const ClientCharts = ({ clientId, clientName = "", mode = "list", bookingId = "", existing = null, onChanged, openId = null, onClosed }) => {
  const { isAdmin } = useAuth();
  const [charts, setCharts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newTpl, setNewTpl] = useState("");
  const [dupFrom, setDupFrom] = useState("");
  const [editor, setEditor] = useState(null); // full chart {template, values, pins, audit,...}
  const [values, setValues] = useState({});
  const [pins, setPins] = useState([]);
  const [title, setTitle] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const photoInput = useRef(null);

  const load = () => (mode === "list" ? api.get(`/clients/${clientId}/charts`).then(({ data }) => setCharts(data)).catch(() => setCharts([])) : Promise.resolve());
  useEffect(() => { load(); if (mode !== "hidden") api.get("/chart-templates").then(({ data }) => setTemplates(data)).catch(() => {}); /* eslint-disable-next-line */ }, [clientId]);
  useEffect(() => { if (openId) openEditor(openId); /* eslint-disable-next-line */ }, [openId]);
  const closeEditor = () => { setEditor(null); onClosed?.(); };

  const openEditor = async (chid) => {
    try {
      const { data } = await api.get(`/charts/${chid}`);
      setEditor(data); setValues(data.values || {}); setPins(data.pins || []); setTitle(data.title || "");
      setShowAudit(false);
      loadPhotos(chid);
    } catch { toast.error("Couldn't open chart"); }
  };
  const loadPhotos = (chid) => api.get(`/clients/${clientId}/photos`, { params: { chart_id: chid } }).then(({ data }) => setPhotos(data)).catch(() => setPhotos([]));

  const createChart = async () => {
    if (!newTpl && !dupFrom) return toast.error("Pick a chart template");
    try {
      const { data } = await api.post(`/clients/${clientId}/charts`, { template_id: newTpl, duplicate_from: dupFrom, booking_id: bookingId || "" });
      setNewOpen(false); setNewTpl(""); setDupFrom(""); load(); onChanged?.();
      openEditor(data.id);
      toast.success("Chart created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create chart"); }
  };

  const saveChart = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/charts/${editor.id}`, { values, pins, title });
      setEditor(data); load(); onChanged?.();
      toast.success("Chart saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  const removeChart = async (chid) => {
    if (!window.confirm("Delete this chart? This can't be undone.")) return;
    try { await api.delete(`/charts/${chid}`); setCharts((cs) => cs.filter((c) => c.id !== chid)); toast.success("Chart deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !editor) return;
    const fd = new FormData();
    fd.append("file", f); fd.append("label", "progress"); fd.append("chart_id", editor.id);
    setUploading(true);
    try { await api.post(`/clients/${clientId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await loadPhotos(editor.id); toast.success("Photo added"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't upload"); }
    finally { setUploading(false); if (photoInput.current) photoInput.current.value = ""; }
  };
  const deletePhoto = async (pid) => {
    try { await api.delete(`/clients/${clientId}/photos/${pid}`); setPhotos((ps) => ps.filter((p) => p.id !== pid)); }
    catch { toast.error("Couldn't remove photo"); }
  };

  const setVal = (fid, v) => setValues((s) => ({ ...s, [fid]: v }));
  const tpl = editor?.template;
  const lastFocusedLong = useRef(null);
  // Quick note tap: append text to the focused (else first) long-text field, prefill matching fields that are still blank.
  const applySnippet = (n) => {
    const fields = tpl?.fields || [];
    const longs = fields.filter((f) => f.type === "long");
    const target = longs.find((f) => f.id === lastFocusedLong.current) || longs[0];
    let filled = 0;
    setValues((s) => {
      const next = { ...s };
      if (target && n.resolved) next[target.id] = (s[target.id] ? `${s[target.id]}\n` : "") + n.resolved;
      const pre = Object.entries(n.prefill || {});
      for (const [lbl, val] of pre) {
        const f = fields.find((x) => (x.label || "").trim().toLowerCase() === lbl.trim().toLowerCase() && !["heading", "text", "long"].includes(x.type));
        if (!f) continue;
        if (next[f.id] === undefined || next[f.id] === "" || next[f.id] === null) {
          next[f.id] = f.type === "checkbox" ? Boolean(val) : f.type === "multichoice" ? (Array.isArray(val) ? val : String(val).split(",").map((x) => x.trim())) : String(val);
          filled += 1;
        }
      }
      return next;
    });
    toast.success(filled ? `Inserted · ${filled} setting${filled > 1 ? "s" : ""} filled` : "Inserted");
  };

  const HIGH_FITZ = ["IV", "V", "VI"];
  const HIGH_ETH = ["Middle Eastern", "North African", "South Asian", "Southeast Asian", "African / Sub-Saharan", "Caribbean", "Hispanic / Latino", "Pacific Islander", "Māori", "Aboriginal / Torres Strait Islander"];
  const skinFlag = useMemo(() => {
    if (tpl?.chart_type !== "laser") return false;
    for (const f of (tpl?.fields || [])) {
      const lab = (f.label || "").toLowerCase();
      const val = values[f.id];
      if (lab.includes("fitzpatrick") && HIGH_FITZ.includes(val)) return true;
      if (lab.startsWith("ethnicity") && HIGH_ETH.includes(val)) return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, tpl]);

  const renderField = (f) => {
    if (f.type === "heading") return <h4 key={f.id} className="font-heading font-semibold text-sm mt-4 mb-1 text-primary">{f.label}</h4>;
    if (f.type === "text") return <p key={f.id} className="text-sm text-muted-foreground">{f.label}</p>;
    const v = values[f.id];
    const lbl = <label className="text-xs font-medium text-muted-foreground">{f.label}{f.required && <span className="text-destructive"> *</span>}</label>;
    if (f.type === "checkbox") return (
      <label key={f.id} className="flex items-center gap-2 text-sm py-1"><Switch checked={!!v} onCheckedChange={(x) => setVal(f.id, x)} data-testid={`chart-field-${f.id}`} />{f.label}{f.required && <span className="text-destructive">*</span>}</label>
    );
    if (f.type === "long") return <div key={f.id} className="space-y-1">{lbl}<Textarea value={v || ""} onFocus={() => { lastFocusedLong.current = f.id; }} onChange={(e) => setVal(f.id, e.target.value)} className="rounded-lg min-h-[70px]" data-testid={`chart-field-${f.id}`} /></div>;
    if (f.type === "choose") return (
      <div key={f.id} className="space-y-1">{lbl}
        <Select value={v || undefined} onValueChange={(x) => setVal(f.id, x)}>
          <SelectTrigger className="h-10 rounded-lg" data-testid={`chart-field-${f.id}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent className="max-h-64">{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
    if (f.type === "multichoice") {
      const arr = Array.isArray(v) ? v : [];
      return (
        <div key={f.id} className="space-y-1">{lbl}
          <div className="flex flex-wrap gap-1.5">
            {(f.options || []).map((o) => {
              const on = arr.includes(o);
              return <button type="button" key={o} onClick={() => setVal(f.id, on ? arr.filter((x) => x !== o) : [...arr, o])}
                className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`} data-testid={`chart-opt-${f.id}-${o}`}>{o}</button>;
            })}
          </div>
        </div>
      );
    }
    return (
      <div key={f.id} className="space-y-1">{lbl}
        <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={v || ""} onChange={(e) => setVal(f.id, e.target.value)} className="h-10 rounded-lg" data-testid={`chart-field-${f.id}`} />
      </div>
    );
  };

  return (
    <div data-testid={mode === "list" ? "client-charts" : `client-charts-${mode}`} className={mode === "list" ? "" : "contents"}>
      {mode === "visit" && (
        <button type="button" onClick={() => setNewOpen(true)} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs hover:bg-muted" data-testid={`visit-chart-new-${bookingId}`}><Plus className="h-3 w-3" />{(existing || []).length ? "Another chart" : "Chart this visit"}</button>
      )}
      {mode === "list" && <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground flex items-center gap-1.5"><Stethoscope className="h-4 w-4" />Treatment charts ({charts.length})</div>
        <Button size="sm" className="rounded-lg" onClick={() => setNewOpen(true)} data-testid="new-chart-button"><Plus className="h-4 w-4 mr-1" />New chart</Button>
      </div>

      {charts.length === 0 && <p className="text-sm text-muted-foreground">No charts yet. Create one to record this client's treatment.</p>}
      <div className="space-y-2">
        {charts.map((c) => (
          <Card key={c.id} className="rounded-xl border p-3 flex items-center gap-3 cursor-pointer hover:border-primary/50" onClick={() => openEditor(c.id)} data-testid={`chart-row-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate flex items-center gap-2">{c.title}
                <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold uppercase tracking-wide">{TYPE_LABEL[c.chart_type] || c.chart_type}</span>
              </div>
              <div className="text-xs text-muted-foreground">{fmtDate(c.created_at)} · {c.practitioner}{c.pin_count ? ` · ${c.pin_count} mark${c.pin_count > 1 ? "s" : ""} on body map` : ""}</div>
            </div>
            {isAdmin && <button onClick={(e) => { e.stopPropagation(); removeChart(c.id); }} className="text-muted-foreground hover:text-destructive" data-testid={`chart-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>}
          </Card>
        ))}
      </div>
      </>}

      {/* New chart dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-chart-dialog">
          <DialogHeader><DialogTitle className="font-heading">New treatment chart</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><label className="text-xs font-medium text-muted-foreground">Chart template</label>
              <Select value={newTpl || undefined} onValueChange={(v) => { setNewTpl(v); setDupFrom(""); }}>
                <SelectTrigger className="h-11 rounded-lg" data-testid="new-chart-template"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({TYPE_LABEL[t.chart_type]})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {charts.length > 0 && (
              <div className="space-y-1"><label className="text-xs font-medium text-muted-foreground">…or duplicate a previous chart</label>
                <Select value={dupFrom || undefined} onValueChange={(v) => { setDupFrom(v); setNewTpl(""); }}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="new-chart-duplicate"><SelectValue placeholder="Duplicate previous" /></SelectTrigger>
                  <SelectContent>{charts.map((c) => <SelectItem key={c.id} value={c.id}>{c.title} — {fmtDate(c.created_at)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button className="rounded-lg" onClick={createChart} data-testid="create-chart-confirm">Create chart</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chart editor */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && closeEditor()}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="chart-editor">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ClipboardList className="h-5 w-5 text-primary" />{tpl?.name || "Chart"}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-3">
              <MedicalAlerts clientId={clientId} compact manage={false} />
              <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>Created {fmtDate(editor.created_at)} by {editor.practitioner}</span>
                {editor.updated_at !== editor.created_at && <span>· Updated {fmtDate(editor.updated_at)}</span>}
                <button className="inline-flex items-center gap-1 text-primary" onClick={() => setShowAudit((s) => !s)} data-testid="chart-audit-toggle"><History className="h-3.5 w-3.5" />Audit ({(editor.audit || []).length})</button>
              </div>
              {showAudit && (
                <div className="rounded-lg border border-border p-2 space-y-1 text-xs bg-muted/30" data-testid="chart-audit-list">
                  {(editor.audit || []).slice().reverse().map((a, i) => (
                    <div key={i} className="flex justify-between gap-2"><span className="capitalize">{a.action}{a.fields_changed ? ` · ${a.fields_changed} field(s)` : ""}</span><span className="text-muted-foreground">{a.by} · {fmtDate(a.at)}</span></div>
                  ))}
                </div>
              )}

              <div className="space-y-1"><label className="text-xs font-medium text-muted-foreground">Chart title</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-10 rounded-lg" data-testid="chart-title" /></div>

              {skinFlag && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 flex items-start gap-2.5" data-testid="skin-safety-flag">
                  <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5 text-amber-600" />
                  <div className="text-sm">
                    <div className="font-semibold">Higher-risk skin for laser</div>
                    <p className="text-[13px] leading-snug mt-0.5">Consider a <b>test patch</b> and <b>conservative settings</b> — lower fluence, longer pulse width, strong cooling. Confirm no recent sun/tanning. Clinical judgement applies; this is a soft prompt, not a block.</p>
                  </div>
                </div>
              )}

              <QuickNotes chartType={tpl?.chart_type} clientId={clientId} excludeChartId={editor.id} context={{ clientName, staffName: editor.practitioner }} onInsert={applySnippet} />
              {editor.prefilled_from && (
                <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm flex items-start gap-2.5" data-testid="chart-prefill-banner">
                  <History className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <div className="flex-1"><span className="font-medium">Pre-filled from last visit</span> ({fmtDate(editor.prefilled_from.created_at)}) — {editor.prefilled_from.zones} zone{editor.prefilled_from.zones === 1 ? "" : "s"} with their settings, device and skin assessment carried over. Check each before saving.</div>
                </div>
              )}
              {editor.previous && (
                <div className="rounded-xl border border-border bg-muted/40 p-3 text-sm" data-testid="chart-previous">
                  <div className="flex items-center justify-between gap-2"><span className="font-medium">Last visit · {fmtDate(editor.previous.created_at)}</span><button type="button" className="text-xs text-primary hover:underline" onClick={() => openEditor(editor.previous.id)} data-testid="chart-previous-open">Open chart</button></div>
                  <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                    {editor.previous.clinical_endpoint && <div><span className="font-medium text-foreground">Endpoint:</span> {editor.previous.clinical_endpoint}</div>}
                    <div><span className="font-medium text-foreground">Skin response:</span> {[].concat(editor.previous.skin_response || []).join(", ") || "—"}</div>
                    <div><span className="font-medium text-foreground">Adverse reactions:</span> {[].concat(editor.previous.adverse_reactions || []).join(", ") || "—"}</div>
                    {editor.previous.zones?.length > 0 && <div><span className="font-medium text-foreground">Zones:</span> {editor.previous.zones.join(", ")}</div>}
                  </div>
                </div>
              )}
              <div className="space-y-2.5">{(tpl?.fields || []).map(renderField)}</div>

              {tpl?.has_body_map && (
                <div className="pt-2"><h4 className="font-heading font-semibold text-sm mb-2 text-primary">Body map</h4>
                  <BodyMap pins={pins} onChange={setPins} /></div>
              )}

              {tpl?.has_photos && (
                <div className="pt-2">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-heading font-semibold text-sm text-primary">Photos</h4>
                    <Button size="sm" variant="outline" className="rounded-lg" disabled={uploading} onClick={() => photoInput.current?.click()} data-testid="chart-photo-upload"><ImagePlus className="h-4 w-4 mr-1" />{uploading ? "Uploading…" : "Add photo"}</Button>
                    <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {photos.map((p) => (
                      <div key={p.id} className="relative group rounded-lg overflow-hidden border border-border">
                        <img src={photoSrc(p.id)} alt="chart" className="w-full h-20 object-cover" />
                        <button onClick={() => deletePhoto(p.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100" data-testid={`chart-photo-delete-${p.id}`}><X className="h-3 w-3" /></button>
                      </div>
                    ))}
                    {photos.length === 0 && <p className="text-xs text-muted-foreground col-span-full">No photos on this chart yet.</p>}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={closeEditor}>Close</Button>
            <Button className="rounded-lg" onClick={saveChart} disabled={saving} data-testid="chart-save"><Save className="h-4 w-4 mr-1" />{saving ? "Saving…" : "Save chart"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
